import {
  Avatar,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  useDisclosure
} from '@nextui-org/react'
import { FaPlus } from '@react-icons/all-files/fa6/FaPlus'
import { LuLoader } from '@react-icons/all-files/lu/LuLoader'
import { MdOutlineCheck } from '@react-icons/all-files/md/MdOutlineCheck'
import { CollectionElement } from '@react-types/shared'
import { useState } from 'react'
import { ChevronDownIcon } from '~/constants/icon'
import { useModalContext } from '~/contexts/ModalContext'
import { useWorkspaceContext, Workspace } from '~/contexts/WorkspaceContext'
import { InitialsAvatar } from '~/components/common/InitialsAvatar'
import { bridge } from '~/services/bridge'
import Typography from '../common/Typography'

export function WorkspaceSwitcher() {
  const { workspaces, currentWorkspace, switchToWorkspace, switching } = useWorkspaceContext()
  const [targetWorkspace, setTargetWorkspace] = useState<Workspace | null>(null) // show spinner
  const { openCreateWorkspace } = useModalContext()
  const isPlugin = bridge.isInPlugin()

  const { onOpenChange, isOpen, onClose } = useDisclosure()

  const onToggleWorkspace = async (workspace: Workspace) => {
    if (!currentWorkspace || workspace.id === currentWorkspace.id) {
      onClose()
      return
    }

    try {
      setTargetWorkspace(workspace)
      await switchToWorkspace(workspace.id)
      onClose()
    } catch (error) {
      console.error('Failed to switch workspace:', error)
    } finally {
      setTargetWorkspace(null)
    }
  }

  // Don't render if no workspaces loaded yet or no current workspace
  if (!workspaces.length || !currentWorkspace) {
    return (
      <div className='self-stretch h-8 px-6 py-1 flex justify-start items-center gap-2'>
        <div className='w-4 h-4 rounded-full bg-gray-200 animate-pulse'></div>
        <div className='flex justify-start items-center gap-1'>
          <div className='text-black/90 text-sm font-medium leading-snug'>
            <div className='w-20 h-4 bg-gray-200 animate-pulse rounded'></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <Dropdown
        onOpenChange={onOpenChange}
        isOpen={isOpen}
        classNames={{
          content: `rounded-xl ${isPlugin ? 'max-w-[calc(100vw-24px)]' : ''}`,
          trigger: 'aria-expanded:scale-100'
        }}
      >
        <DropdownTrigger>
          <div
            className={`${isPlugin ? 'py-2 px-3' : 'py-4 px-6'} hover:bg-gray-100 cursor-pointer flex items-center gap-2 w-full transform-none`}
            aria-label='Workspace switcher'
          >
            <Typography
              fontFamily='geist'
              fontSize='titleSm'
              fontWeight='bold'
              className='text-tokens-neutral-text-primary text-center truncate select-none'
            >
              {currentWorkspace.name}
            </Typography>
            <ChevronDownIcon className='w-3 h-3' />
          </div>
        </DropdownTrigger>
        <DropdownMenu
          disableAnimation={true}
          closeOnSelect={false}
          className='overflow-y-scroll max-h-[66vh] caret-transparent'
        >
          <DropdownItem
            key='add'
            textValue='Create new workspace'
            onClick={openCreateWorkspace}
            className='!text-tokens-neutral-text-primary'
          >
            <div className='flex items-center space-x-2 p-1'>
              <Avatar
                fallback={<FaPlus />}
                className='bg-gray-200 dark:bg-neutral-700 h-[40px] w-[40px] rounded-xl'
              />
              <Typography
                aria-label='Create new workspace'
                fontFamily='geist'
                fontSize='bodyLg'
                fontWeight='medium'
                className='!text-tokens-neutral-text-primary'
              >
                Create new workspace
              </Typography>
            </div>
          </DropdownItem>
          {
            workspaces.map(workspace => (
              <DropdownItem
                key={workspace.id}
                textValue={workspace.name}
                closeOnSelect={false}
                isDisabled={switching}
                onClick={() => onToggleWorkspace(workspace)}
                className='!text-tokens-neutral-text-primary'
              >
                <div className='flex justify-between items-center'>
                  <div className='flex items-center space-x-2 p-1'>
                    <InitialsAvatar name={workspace.name ?? ''} size='md' alt='Workspace avatar' />
                    <Typography
                      fontFamily='geist'
                      fontSize='bodyLg'
                      fontWeight='medium'
                      className='!text-tokens-neutral-text-primary'
                    >
                      {workspace.name}
                    </Typography>
                  </div>
                  {currentWorkspace.id === workspace.id ? (
                    <MdOutlineCheck className='text-tokens-neutral-text-primary' />
                  ) : null}
                  {switching && targetWorkspace?.id === workspace.id && (
                    <LuLoader size={12} className='mr-1 animate-spin' />
                  )}
                </div>
              </DropdownItem>
            )) as unknown as CollectionElement<object>
          }
        </DropdownMenu>
      </Dropdown>
    </>
  )
}
